import React, { useState, useEffect } from 'react';
import {
  addDoc, onSnapshot, query, orderBy,
  updateDoc, serverTimestamp, deleteDoc
} from 'firebase/firestore';
import { tasksColl, taskDoc } from '../db';
import { UserProfile, Screen } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface Task {
  id: string;
  title: string;
  completed: boolean;
  completedBy?: string;
  completedByName?: string;
  weekKey: string;
  createdBy: string;
  timestamp: any;
}

function getWeekKey(date = new Date()) {
  const jan1 = new Date(date.getFullYear(), 0, 1);
  const week = Math.ceil(((date.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
  return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

function getWeekLabel(weekKey: string): string {
  const current = getWeekKey();
  if (weekKey === current) return 'Bu Hafta';
  const [year, wStr] = weekKey.split('-W');
  const week = parseInt(wStr);
  const d = new Date(parseInt(year), 0, 1 + (week - 1) * 7);
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmt = (dt: Date) => dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} — ${fmt(sun)}`;
}

export default function Tasks({ user, navigate, backTo = 'us' }: Props) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTask, setNewTask] = useState('');
  const [adding, setAdding] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const currentWeek = getWeekKey();

  useEffect(() => {
    const q = query(tasksColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() } as Task)));
    });
  }, [user.coupleId]);

  const addTask = async () => {
    if (!newTask.trim()) return;
    setAdding(true);
    await addDoc(tasksColl(user.coupleId), {
      title: newTask.trim(),
      completed: false,
      weekKey: currentWeek,
      createdBy: user.uid,
      timestamp: serverTimestamp(),
    });
    setNewTask('');
    setAdding(false);
  };

  const toggle = async (task: Task) => {
    await updateDoc(taskDoc(user.coupleId, task.id), {
      completed: !task.completed,
      completedBy: !task.completed ? user.uid : null,
      completedByName: !task.completed ? user.displayName : null,
    });
  };

  const remove = async (id: string) => {
    await deleteDoc(taskDoc(user.coupleId, id));
  };

  // Group by weekKey
  const grouped: Record<string, Task[]> = {};
  tasks.forEach(t => {
    if (!grouped[t.weekKey]) grouped[t.weekKey] = [];
    grouped[t.weekKey].push(t);
  });

  const allWeeks = Object.keys(grouped).sort().reverse();
  const thisWeekTasks = grouped[currentWeek] || [];
  const oldWeeks = allWeeks.filter(w => w !== currentWeek);

  const thisWeekDone = thisWeekTasks.filter(t => t.completed).length;

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Görevler</h1>
      </div>

      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Add task */}
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            placeholder="Yeni görev ekle..."
            value={newTask}
            onChange={e => setNewTask(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTask()}
            style={{ flex: 1 }}
          />
          <button onClick={addTask} disabled={adding || !newTask.trim()} style={styles.addBtn}>+</button>
        </div>

        {/* This week */}
        <div>
          <div style={styles.weekHeader}>
            <span style={styles.weekLabel}>Bu Hafta</span>
            {thisWeekTasks.length > 0 && (
              <span style={styles.weekCount}>{thisWeekDone}/{thisWeekTasks.length}</span>
            )}
          </div>

          {thisWeekTasks.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {thisWeekTasks.map(task => <TaskRow key={task.id} task={task} user={user} onToggle={toggle} onRemove={remove} />)}
            </div>
          ) : (
            <div style={styles.emptyWeek}>
              <p>Bu hafta için görev yok</p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Yukarıdan ekleyebilirsin</p>
            </div>
          )}
        </div>

        {/* Old weeks */}
        {oldWeeks.length > 0 && (
          <div>
            <button onClick={() => setShowOld(v => !v)} style={styles.toggleOld}>
              {showOld ? '▲' : '▼'} Önceki Görevler ({oldWeeks.reduce((s, w) => s + (grouped[w]?.length || 0), 0)})
            </button>

            {showOld && oldWeeks.map(week => (
              <div key={week} style={{ marginTop: 16 }}>
                <div style={styles.weekHeader}>
                  <span style={{ ...styles.weekLabel, opacity: 0.6 }}>{getWeekLabel(week)}</span>
                  <span style={{ ...styles.weekCount, opacity: 0.5 }}>
                    {grouped[week].filter(t => t.completed).length}/{grouped[week].length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {grouped[week].map(task => <TaskRow key={task.id} task={task} user={user} onToggle={toggle} onRemove={remove} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, user, onToggle, onRemove }: {
  task: Task; user: UserProfile;
  onToggle: (t: Task) => void; onRemove: (id: string) => void;
}) {
  return (
    <div className="card" style={{ ...rowStyles.wrap, opacity: task.completed ? 0.65 : 1 }}>
      <button onClick={() => onToggle(task)} style={rowStyles.check}>
        <span style={{ ...rowStyles.checkMark, ...(task.completed ? rowStyles.checkMarkDone : {}) }}>{task.completed ? '✓' : ''}</span>
      </button>
      <div style={{ flex: 1 }}>
        <p style={{ ...rowStyles.title, textDecoration: task.completed ? 'line-through' : 'none' }}>
          {task.title}
        </p>
        {task.completed && task.completedByName && (
          <p style={rowStyles.by}>{task.completedByName} tamamladı</p>
        )}
      </div>
      {task.createdBy === user.uid && (
        <button onClick={() => onRemove(task.id)} style={rowStyles.del}>×</button>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  addBtn: {
    width: 50, height: 50, borderRadius: 16, flexShrink: 0,
    background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
    border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 24, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 14px 34px rgba(79,70,229,0.28)',
  },
  weekHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  weekLabel: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: 0.5 },
  weekCount: { fontSize: 12, color: '#67E8F9', fontWeight: 800 },
  emptyWeek: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '20px', color: 'rgba(255,255,255,0.4)', fontSize: 14, textAlign: 'center',
    background: 'rgba(255,255,255,0.03)', borderRadius: 14, border: '1px dashed rgba(255,255,255,0.08)',
  },
  toggleOld: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
    fontSize: 13, cursor: 'pointer', padding: '4px 0',
  },
};

const rowStyles: Record<string, React.CSSProperties> = {
  wrap: { padding: '13px 14px', display: 'flex', alignItems: 'center', gap: 12, borderRadius: 18 },
  check: { background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 },
  checkMark: {
    width: 24, height: 24, borderRadius: 9, display: 'grid', placeItems: 'center',
    border: '1px solid rgba(255,255,255,0.16)', color: 'white', fontSize: 15, fontWeight: 900,
    background: 'rgba(255,255,255,0.045)',
  },
  checkMarkDone: {
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  title: { fontSize: 15, color: 'white' },
  by: { fontSize: 11, color: '#67E8F9', marginTop: 3, fontWeight: 700 },
  del: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 20, cursor: 'pointer', paddingLeft: 8 },
};
